import React, { useMemo, useState } from "react";
import { api } from "../utils/api";
import { useI18n } from "../context/I18nContext";
import { useApp } from "../context/AppContext";

const MAIN_KEYS = ["calories", "carbs", "protein", "fat", "fiber", "sodium", "potassium", "magnesium"];
const DETAIL_KEYS = ["sugar", "calcium", "iron", "vitaminC", "vitaminA", "vitaminD", "vitaminB12"];
const DAILY_CHART_KEYS = ["calories", "carbs", "protein", "fat", "fiber", "sodium", "potassium", "magnesium", "calcium", "iron"];
const MACRO_KEYS = ["carbs", "protein", "fat"];
const MACRO_COLORS = {
  carbs: "#E8703A",
  protein: "#4A7C59",
  fat: "#8A5A44"
};

function formatValue(nutrient) {
  if (!nutrient || nutrient.value === null || nutrient.value === undefined) return "-";
  return `${nutrient.value} ${nutrient.unit}`;
}

function confidenceLabel(t, confidence) {
  if (confidence === "high") return t("nutritionConfidenceHigh");
  if (confidence === "medium") return t("nutritionConfidenceMedium");
  return t("nutritionConfidenceLow");
}

export default function NutritionSearch() {
  const { t } = useI18n();
  const { actions } = useApp();
  const [query, setQuery] = useState("");
  const [searched, setSearched] = useState("");
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mealText, setMealText] = useState("yumurta, 100\nzeytinyağı, 10\ndomates, 120");
  const [mealEstimate, setMealEstimate] = useState(null);
  const [mealLoading, setMealLoading] = useState(false);
  const [mealLimitInfo, setMealLimitInfo] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoEstimate, setPhotoEstimate] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoLimitInfo, setPhotoLimitInfo] = useState(null);

  const selected = useMemo(() => items[0] || null, [items]);

  const parseMealIngredients = () => mealText
    .split(/\n+/)
    .map((line) => {
      const clean = line.trim();
      const match = clean.match(/^(.*?)[,\s]+(\d+(?:[.,]\d+)?)\s*(g|gr|gram)?$/i);
      if (!match) return null;
      return {
        name: match[1].replace(/[,;-]+$/g, "").trim(),
        grams: Number(match[2].replace(",", "."))
      };
    })
    .filter((item) => item?.name && item.grams > 0);

  const search = async (refresh = false) => {
    if (query.trim().length < 2 || loading) return;
    setLoading(true);
    setError("");
    try {
      const response = await api.searchNutrition(query.trim(), { refresh, limit: 6 });
      setItems(response.data || []);
      setMeta(response.meta || null);
      setSearched(query.trim());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const estimateMeal = async () => {
    const ingredients = parseMealIngredients();
    if (!ingredients.length || mealLoading) return;
    setMealLoading(true);
    setError("");
    setMealLimitInfo(null);
    try {
      const response = await api.estimateMealNutrition(ingredients, actions.usageProfile());
      setMealEstimate(response.data);
    } catch (err) {
      if (err?.code === "LIMIT_REACHED") {
        setMealLimitInfo(err.usage || {});
      } else {
        setError(err.message);
      }
    } finally {
      setMealLoading(false);
    }
  };

  const handlePhoto = (file) => {
    setPhotoFile(file || null);
    setPhotoEstimate(null);
    setPhotoLimitInfo(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(file ? URL.createObjectURL(file) : "");
  };

  const estimatePhoto = async () => {
    if (!photoFile || photoLoading) return;
    setPhotoLoading(true);
    setError("");
    setPhotoLimitInfo(null);
    try {
      const response = await api.estimatePhotoNutrition(photoFile, actions.usageProfile());
      setPhotoEstimate(response.data);
    } catch (err) {
      if (err?.code === "LIMIT_REACHED") {
        setPhotoLimitInfo(err.usage || {});
      } else {
        setError(err.message);
      }
    } finally {
      setPhotoLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>{t("nutrition")}</h1>
        <p>{t("nutritionIntro")}</p>
      </div>

      <div className="nutrition-search-panel">
        <div className="search-box">
          <span>🔍</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && search(false)}
            placeholder={t("nutritionSearchPlaceholder")}
          />
          {query && <button onClick={() => setQuery("")}>×</button>}
        </div>
        <button className="btn btn-primary" onClick={() => search(false)} disabled={loading || query.trim().length < 2}>
          {loading ? t("searching") : t("searchNutrition")}
        </button>
        {searched && (
          <button className="btn btn-outline" onClick={() => search(true)} disabled={loading}>
            {t("refreshFromSources")}
          </button>
        )}
      </div>

      {error && <div className="reward-box"><strong>{t("requestFailed")}</strong><span>{error}</span></div>}

      {meta?.warning && <p className="nutrition-warning">{meta.warning}</p>}

      <div className="meal-estimate-grid">
        <section className="nutrition-tool-card">
          <div className="nutrition-card-head">
            <div>
              <h2>{t("mealNutritionEstimate")}</h2>
              <p>{t("mealNutritionEstimateHint")}</p>
            </div>
          </div>
          <textarea
            value={mealText}
            onChange={(event) => setMealText(event.target.value)}
            placeholder={t("mealIngredientsPlaceholder")}
          />
          <div className="nutrition-tool-actions">
            <button className="btn btn-primary" onClick={estimateMeal} disabled={mealLoading || !parseMealIngredients().length}>
              {mealLoading ? t("calculating") : t("calculateMealNutrition")}
            </button>
            <span>{t("ingredientGramFormat")}</span>
          </div>
          {mealLimitInfo && (
            <div className="reward-box">
              <strong>{t("mealNutritionLimitReached")}</strong>
              <span>Günlük kullanım hakkın gece 00.00'da yenilenir.</span>
            </div>
          )}
          {mealEstimate && <EstimateResult result={mealEstimate} t={t} />}
        </section>

        <section className="nutrition-tool-card">
          <div className="nutrition-card-head">
            <div>
              <h2>{t("photoNutritionEstimate")}</h2>
              <p>{t("photoNutritionEstimateHint")}</p>
            </div>
          </div>
          <div className="photo-estimate-upload">
            {photoPreview ? <img src={photoPreview} alt={t("photoNutritionEstimate")} /> : <span>{t("photoSourceHint")}</span>}
          </div>
          <div className="photo-source-actions">
            <label className="btn btn-outline">
              {t("takeMealPhoto")}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                onChange={(event) => handlePhoto(event.target.files?.[0])}
              />
            </label>
            <label className="btn btn-outline">
              {photoFile ? t("changeMealPhoto") : t("chooseFromGallery")}
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => handlePhoto(event.target.files?.[0])} />
            </label>
          </div>
          <div className="nutrition-tool-actions">
            <button className="btn btn-primary" onClick={estimatePhoto} disabled={photoLoading || !photoFile}>
              {photoLoading ? t("analyzingPhoto") : t("estimateFromPhoto")}
            </button>
            <span>{t("photoEstimateWarning")}</span>
          </div>
          {photoLimitInfo && (
            <div className="reward-box">
              <strong>{t("photoNutritionLimitReached")}</strong>
              <span>Günlük AI kullanım hakkın gece 00.00'da yenilenir.</span>
            </div>
          )}
          {photoEstimate && <EstimateResult result={photoEstimate} t={t} />}
        </section>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /><p>{t("nutritionLoading")}</p></div>
      ) : items.length > 0 ? (
        <div className="nutrition-layout">
          <aside className="nutrition-results">
            {items.map((item) => (
              <button
                key={item.id}
                className={`nutrition-result ${selected?.id === item.id ? "active" : ""}`}
                onClick={() => setItems((current) => [item, ...current.filter((entry) => entry.id !== item.id)])}
              >
                <strong>{item.name}</strong>
                {item.brand && <span>{item.brand}</span>}
                <small>{item.source} · {confidenceLabel(t, item.confidence)}</small>
              </button>
            ))}
          </aside>

          {selected && (
            <section className="nutrition-card">
              <div className="nutrition-card-head">
                <div>
                  <h2>{selected.name}</h2>
                  <p>{t("nutritionPer100g")}</p>
                </div>
                <div className={`source-badge ${selected.confidence}`}>{confidenceLabel(t, selected.confidence)}</div>
              </div>

              <div className="nutrition-source-line">
                <span>{selected.source}</span>
                {selected.sourceUrl && <a href={selected.sourceUrl} target="_blank" rel="noreferrer">{t("viewSource")}</a>}
              </div>

              <div className="nutrient-grid">
                {MAIN_KEYS.map((key) => (
                  <NutrientTile key={key} nutrient={selected.nutrients[key]} />
                ))}
              </div>

              <div className="nutrition-charts">
                <DailyValueChart nutrients={selected.nutrients} title={t("dailyValueChart")} />
                <MacroDonut nutrients={selected.nutrients} title={t("macroDistribution")} />
              </div>

              <h3 className="suggest-section-title">{t("vitaminsMinerals")}</h3>
              <div className="nutrient-table">
                {[...DETAIL_KEYS].map((key) => (
                  <NutrientRow key={key} nutrient={selected.nutrients[key]} />
                ))}
              </div>

              <p className="nutrition-license">{selected.sourceLicense}</p>
            </section>
          )}
        </div>
      ) : searched ? (
        <div className="empty-state">
          <div className="empty-icon">100g</div>
          <h3>{t("nutritionNotFound")}</h3>
          <p>{t("nutritionNotFoundHint")}</p>
        </div>
      ) : null}
    </div>
  );
}

function EstimateResult({ result, t }) {
  const title = result.dishName || t("estimatedNutrition");
  return (
    <div className="estimate-result">
      <div className="nutrition-card-head">
        <div>
          <h3>{title}</h3>
          <p>
            {result.servingGrams ? `${Math.round(result.servingGrams)} g` : t("estimatedServing")} · {confidenceLabel(t, result.confidence)}
          </p>
        </div>
        <div className={`source-badge ${result.confidence || "low"}`}>{confidenceLabel(t, result.confidence)}</div>
      </div>
      {result.warning && <p className="nutrition-warning">{result.warning}</p>}
      {result.detectedFoods?.length > 0 && (
        <div className="detected-foods">
          {result.detectedFoods.map((item) => <span key={item}>{item}</span>)}
        </div>
      )}
      <div className="nutrient-grid">
        {MAIN_KEYS.map((key) => (
          <NutrientTile key={key} nutrient={result.nutrients?.[key]} />
        ))}
      </div>
      <div className="nutrition-charts">
        <DailyValueChart nutrients={result.nutrients || {}} title={t("dailyValueChart")} />
        <MacroDonut nutrients={result.nutrients || {}} title={t("macroDistribution")} />
      </div>
      {result.ingredients?.length > 0 && (
        <div className="ingredient-estimate-list">
          {result.ingredients.map((item, index) => (
            <div key={`${item.name}-${index}`}>
              <strong>{item.name} · {item.grams}g</strong>
              <span>{item.matched ? `${item.matchName} · ${item.source}` : item.message}</span>
            </div>
          ))}
        </div>
      )}
      {result.notes && <p className="nutrition-license">{result.notes}</p>}
    </div>
  );
}

function DailyValueChart({ nutrients, title }) {
  const rows = DAILY_CHART_KEYS
    .map((key) => nutrients[key])
    .filter((nutrient) => nutrient && nutrient.dailyValuePercent !== null && nutrient.dailyValuePercent !== undefined);

  if (!rows.length) return null;

  return (
    <div className="chart-card">
      <h3>{title}</h3>
      <div className="bar-chart">
        {rows.map((nutrient) => {
          const percent = Math.max(0, Number(nutrient.dailyValuePercent) || 0);
          return (
            <div className="bar-row" key={nutrient.key}>
              <span>{nutrient.label}</span>
              <div className="bar-track">
                <div className="bar-fill" style={{ width: `${Math.min(percent, 100)}%` }} />
              </div>
              <strong>%{percent}</strong>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MacroDonut({ nutrients, title }) {
  const macros = MACRO_KEYS.map((key) => ({
    key,
    label: nutrients[key]?.label || key,
    value: Math.max(0, Number(nutrients[key]?.value || 0)),
    color: MACRO_COLORS[key]
  })).filter((item) => item.value > 0);
  const total = macros.reduce((sum, item) => sum + item.value, 0);
  if (!total) return null;

  let cursor = 0;
  const gradient = macros.map((item) => {
    const start = cursor;
    const end = cursor + (item.value / total) * 100;
    cursor = end;
    return `${item.color} ${start}% ${end}%`;
  }).join(", ");

  return (
    <div className="chart-card macro-card">
      <h3>{title}</h3>
      <div className="macro-chart-wrap">
        <div className="macro-donut" style={{ background: `conic-gradient(${gradient})` }}>
          <div>{Math.round(total * 10) / 10}g</div>
        </div>
        <div className="macro-legend">
          {macros.map((item) => (
            <div key={item.key}>
              <span style={{ background: item.color }} />
              <strong>{item.label}</strong>
              <em>%{Math.round((item.value / total) * 100)}</em>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function NutrientTile({ nutrient }) {
  return (
    <div className="nutrient-tile">
      <span>{nutrient?.label}</span>
      <strong>{formatValue(nutrient)}</strong>
      <small>{nutrient?.dailyValuePercent === null || nutrient?.dailyValuePercent === undefined ? "-" : `%${nutrient.dailyValuePercent}`}</small>
    </div>
  );
}

function NutrientRow({ nutrient }) {
  return (
    <div className="nutrient-row">
      <span>{nutrient?.label}</span>
      <strong>{formatValue(nutrient)}</strong>
      <em>{nutrient?.dailyValuePercent === null || nutrient?.dailyValuePercent === undefined ? "-" : `%${nutrient.dailyValuePercent}`}</em>
    </div>
  );
}
